'use client';
import { useRef, useState } from 'react';
import { X, Star, Plus } from 'lucide-react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Input } from '@/components/ui/Input';
import SpecialtyPicker from './SpecialtyPicker';
import { specialtyLabel } from '@/lib/specialties/search';
import { MAX_SECONDARY_SPECIALTIES, type HealthcareRole, type SpecialtyOption } from '@/lib/specialties/types';

export interface SpecialtiesValue { primary: string | null; secondary: string[]; other: string }
export interface SpecialtiesFieldProps {
  value: SpecialtiesValue; onChange: (v: SpecialtiesValue) => void; options: SpecialtyOption[];
  role: HealthcareRole | null; required?: boolean; error?: string; disabled?: boolean; compact?: boolean;
}

export default function SpecialtiesField({ value, onChange, options, role, required, error, disabled, compact }: SpecialtiesFieldProps) {
  const { t, language } = useLanguage();
  const [adding, setAdding] = useState(false);
  // The add-another chip only animates once a specialty is picked here, not for one already saved.
  const initialPrimary = useRef(value.primary);
  const byId = new Map(options.map(o => [o.id, o]));
  const primaryIsOther = value.primary ? byId.get(value.primary)?.isOther ?? false : false;

  const setPrimary = (id: string | null) => {
    const secondary = value.secondary.filter(s => s !== id);
    const other = id && byId.get(id)?.isOther ? value.other : '';
    onChange({ primary: id, secondary, other });
  };
  const addSecondary = (id: string | null) => {
    setAdding(false);
    if (!id || value.secondary.includes(id) || id === value.primary) return;
    onChange({ ...value, secondary: [...value.secondary, id].slice(0, MAX_SECONDARY_SPECIALTIES) });
  };
  const remove = (id: string) => onChange({ ...value, secondary: value.secondary.filter(s => s !== id) });
  const makePrimary = (id: string) => {
    const secondary = value.secondary.map(s => (s === id ? value.primary : s)).filter((s): s is string => !!s);
    onChange({ primary: id, secondary, other: byId.get(id)?.isOther ? value.other : '' });
  };

  return (
    <div className="space-y-2">
      <SpecialtyPicker value={value.primary} onChange={setPrimary} options={options} exclude={value.secondary}
        label={role === 'student' ? t('profile.specialtyInTraining') : t('profile.primarySpecialty')}
        required={required} error={error} disabled={disabled} />
      {primaryIsOther && (
        <Input label={t('profile.specifySpecialty')} value={value.other} maxLength={120}
          onChange={e => onChange({ ...value, other: e.target.value })} placeholder={t('profile.specifySpecialtyPlaceholder')} />
      )}
      {value.secondary.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label={t('profile.secondarySpecialties')}>
          {value.secondary.map(id => {
            const o = byId.get(id);
            return (
              <li key={id} className="inline-flex items-center gap-1 rounded-full bg-gray-100 pl-3 pr-1 py-1 text-sm text-gray-800">
                <span>{o ? specialtyLabel(o, language) : id}</span>
                {!disabled && (
                  <>
                    <button type="button" aria-label={t('profile.makePrimary')} title={t('profile.makePrimary')} onClick={() => makePrimary(id)} className="p-1 rounded-full hover:bg-gray-200"><Star className="h-3.5 w-3.5" /></button>
                    <button type="button" aria-label={t('profile.removeSpecialty')} title={t('profile.removeSpecialty')} onClick={() => remove(id)} className="p-1 rounded-full hover:bg-gray-200"><X className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {adding && (
        <SpecialtyPicker value={null} onChange={addSecondary} options={options} exclude={[value.primary ?? '', ...value.secondary]} disabled={disabled} />
      )}
      {!adding && !disabled && value.primary && value.secondary.length < MAX_SECONDARY_SPECIALTIES && (
        <button key={value.primary} type="button" onClick={() => setAdding(true)}
          className={`inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-dashed border-blue-300 bg-blue-50 pl-2.5 pr-3.5 py-1.5 font-semibold text-blue-700 hover:bg-blue-100 ${compact ? 'text-xs' : 'text-sm'} ${value.primary !== initialPrimary.current ? 'specialty-chip-in' : ''}`}>
          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />{t('profile.addAnotherSpecialty')}
        </button>
      )}
    </div>
  );
}
